import { Box, Typography, Card } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { useStoreDetails } from "../../common/Providers/StoreDetailsProvider/UseStoreDetails";
import { useVehicleReservation } from "../../common/Providers/VehicleReservationProvider/UseVehicleReservation";
import { useState, useEffect } from "react";
import { API_URL } from "../../common/AppConstants";

const HorizontalCard = ({ category, onSelect, isSelected, primaryColor }: { category: any; onSelect: (category: any) => void; isSelected: boolean; primaryColor: string }) => {
    const [imageError, setImageError] = useState(false);
    const [hovered, setHovered] = useState(false);

    return (
        <Card
            sx={{
                ...horizontalCardStyles.container,
                outline: isSelected ? `3px solid ${primaryColor}` : '3px solid transparent',
            }}
            onClick={() => onSelect(category)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Box sx={horizontalCardStyles.imageContainer}>
                {imageError ? (
                    <FontAwesomeIcon icon={faImage} style={{ width: 60, height: 60, color: "#ccc" }} />
                ) : (
                    <img
                        src={API_URL + category.img_url}
                        alt={category.category}
                        title={category.category}
                        loading="lazy"
                        onError={() => setImageError(true)}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            transition: "transform 0.3s ease",
                            transform: hovered ? "scale(1.05)" : "scale(1)",
                        }}
                    />
                )}
                <Box sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: hovered
                        ? "rgba(0,0,0,0.65)"
                        : isSelected
                            ? `linear-gradient(transparent, ${primaryColor}cc)`
                            : "linear-gradient(transparent, rgba(0,0,0,0.55))",
                    padding: "20px 8px 8px",
                    transition: "background 0.3s ease",
                }}>
                    <Typography sx={horizontalCardStyles.categoryName}>
                        {category.category}
                    </Typography>
                </Box>
            </Box>
        </Card>
    );
};

const CategorySelector = ({ primaryColor = "#2196f3" }: { primaryColor?: string }) => {
    const { setVehicleReservationValue, VehicleReservationMain } = useVehicleReservation();
    const { categories } = useStoreDetails();
    const [selectedCategory, setSelectedCategory] = useState<any>(null);

    useEffect(() => {
        if (categories && categories.length > 0 && !selectedCategory) {
            // If there's already a mainCategory set (e.g., from cart items), use that
            if (VehicleReservationMain.mainCategory) {
                const savedCategory = categories.find((cat: any) => cat.id === VehicleReservationMain.mainCategory);
                if (savedCategory) {
                    setSelectedCategory(savedCategory);
                    return;
                }
            }
            
            // Otherwise, select the first category
            const firstCategory = categories[0];
            setSelectedCategory(firstCategory);
            setVehicleReservationValue("mainCategory", firstCategory.id);
        }
    }, [categories, selectedCategory, setVehicleReservationValue, VehicleReservationMain.mainCategory]);

    const handleSelectCategory = (category: any) => {
        setSelectedCategory(category);
        setVehicleReservationValue("mainCategory", category.id);
    };

    return (
        <Box sx={styles.contentContainer}>
            <Box sx={styles.scrollContainer}>
                {categories.map((category: any, index: number) => (
                    <HorizontalCard
                        key={category.id + "_" + index}
                        category={category}
                        onSelect={handleSelectCategory}
                        isSelected={selectedCategory?.id === category.id}
                        primaryColor={primaryColor}
                    />
                ))}
            </Box>
        </Box>
    )
}

const styles = {
    contentContainer: {
        flex: 1,
        backgroundColor: "white",
    },
    scrollContainer: {
        display: "flex",
        flexDirection: "row",
        padding: "10px 4px",
        gap: 2,
        overflowX: "auto",
        scrollSnapType: "x mandatory",
        "&::-webkit-scrollbar": {
            height: "8px",
        },
        "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
            borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#a8a8a8",
        },
    },
    contentTitle: {
        fontSize: "24px",
        textAlign: "center",
        fontWeight: 600,
        mb: 2,
    },
};

const horizontalCardStyles = {
    container: {
        cursor: "pointer",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        transition: "box-shadow 0.3s ease",
        width: { xs: "110px", sm: "150px" },
        flexShrink: 0,
        scrollSnapAlign: "start",
        padding: "0",
        borderRadius: 2,
        overflow: "hidden",
        border: "none",
        "&:hover": {
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
        },
    },
    imageContainer: {
        width: "100%",
        height: { xs: "110px", sm: "150px" },
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f0f0f0",
    },
    categoryName: {
        fontSize: "13px",
        fontWeight: 700,
        color: "#fff",
        textAlign: "center",
        lineHeight: 1.3,
        textShadow: "0 1px 3px rgba(0,0,0,0.5)",
    },
};


export default CategorySelector;